import React, { useState, useEffect, useRef } from "react";
import MenuHeader from "./MenuHeader";
import Modal from 'react-modal';
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { castDate, asTitle } from '../shared/utils/string';
import Swal from 'sweetalert2';
import PromotionService from "../services/promotion.service";
import UserService from "../services/user.service";
import Select from 'react-select';
import ProductService from "../services/product.service";

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    scrollbarWidth: 'none',
    height: '680px',
  },
};

const customSelectStyles = {
  control: (styles) => ({
    ...styles,
    width: '100%',
    height: '60px',
    padding: '0 15px',
    fontSize: '14px',
    lineHeight: '60px',
    color: '#393D44',
    border: '0',
    borderRadius: '20px',
    background: '#ffffff',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: 'none',
  }),
};

const Promotion = () => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState(null); // For editing
  const axiosPrivate = useAxiosPrivate();

  const [promotionFormData, setPromotionFormData] = useState({
    id: '',
    name: '',
    code: '',
    date_validity: '',
    date_creation: '',
    isUnlimited: false,
    type: 'PERCENT_OFF',
    amount: '',
    assigned_to: 'One client',
    total_usage: '',
    usage_per_customer: '',
    notifyClients: false,
    clients: [],
    product_id: null,
    is_active: 1,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState({
    with: 'clients,product',
    page: currentPage,
    per_page: perPage
  });

  const [sortDirection, setSortDirection] = useState(true);
  const [filterData, setFilterData] = useState({
    search: "",
  });

  const timeoutRef = useRef(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [productList, setProductList] = useState([]);

  useEffect(() => {
    fetchPromotions();
  }, [currentPage, perPage]);

  const fetchPromotions = async (updatedFilters = filters) => {
    try {
      const response = await PromotionService.index(updatedFilters, axiosPrivate);
      setPromotions(response.data);
      setTotal(response.total);
      setLastPage(response.last_page);
      if (response.last_page < currentPage) {
        setFilters({ ...updatedFilters, page: response.last_page })
        setCurrentPage(response.last_page);
      }
    } catch (error) {
      console.error("Error fetching promotions", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await UserService.indexByRole({ with: 'primary_address', status: '!eq:BLOCKED' }, 'ROLE_CLIENT', axiosPrivate);
      setClients(response);
    } catch (error) {
      console.error("Error fetching clients", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'assigned_to') {
      setPromotionFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
        clients: [],
      }));
    } else {
      setPromotionFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleClientsChange = (selected) => {
    if (promotionFormData.assigned_to === "One client") {
      setPromotionFormData((prev) => ({
        ...prev,
        clients: selected ? [selected.value] : [],
      }));
    } else if (promotionFormData.assigned_to === "Group of clients") {
      setPromotionFormData((prev) => ({
        ...prev,
        clients: selected ? selected.map((client) => client.value) : [],
      }));
    }
  };

  const openModal = async (promotion = null) => {
    if (promotion) {
      setSelectedPromotion(promotion);
      setPromotionFormData({
        ...promotion,
        clients: promotion.clients.map((client) => client.id),
        assigned_to: promotion.clients.length > 1 ? 'Group of clients' : 'One client',
      });
    } else {
      setSelectedPromotion(null);
      setPromotionFormData({
        name: '',
        code: generatePromoCode(),
        date_validity: '',
        date_creation: '',
        isUnlimited: false,
        type: 'PERCENT_OFF',
        amount: '',
        assigned_to: 'One client',
        total_usage: '',
        usage_per_customer: '',
        notifyClients: false,
        clients: [],
        product_id: null,
        is_active: 1,
      });
    }
    const products = await ProductService.index(null, axiosPrivate);
    setProductList(products);
    await fetchClients();
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const generatePromoCode = (length = 10) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let promoCode = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      promoCode += characters.charAt(randomIndex);
    }
    return promoCode;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = selectedPromotion
        ? await PromotionService.update(selectedPromotion.id, {
          ...promotionFormData,
          code: promotionFormData.code || selectedPromotion.code,
        }, axiosPrivate)
        : await PromotionService.store(promotionFormData, axiosPrivate);
      fetchPromotions();
      closeModal();
    } catch (error) {

    }
  };

  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: client.extra?.full_name,
  }));

  const selectedClients = clientOptions.filter((option) => promotionFormData.clients.includes(option.value));

  const handleNextPage = () => {
    setFilters({ ...filters, page: currentPage + 1 });
    setCurrentPage(currentPage + 1);
  }

  const handlePrevPage = () => {
    setFilters({ ...filters, page: currentPage - 1 });
    setCurrentPage(currentPage - 1);
  }

  const handleLastPage = () => {
    setFilters({ ...filters, page: lastPage });
    setCurrentPage(lastPage);
  }

  const handleFirstPage = () => {
    setFilters({ ...filters, page: 1 });
    setCurrentPage(1);
  }

  const handlePerPageChange = (e) => {
    setFilters({ ...filters, per_page: e.target.value, page: 1 });
    setPerPage(e.target.value);
    setCurrentPage(1);
  }

  const handleSelectedSort = async (event) => {
    const selected = document.querySelector('.sort-header.active');
    let direction = true;
    if (selected?.id !== event.target.id) {
      selected?.classList.remove('active');
      event.target.classList.add('active');
      setSortDirection(true);
    } else {
      setSortDirection(!sortDirection);
      direction = !sortDirection;
    }

    const sortField = event.target.id === 'name' ? `${direction ? '' : '-'}name` : `${direction ? '' : '-'}${event.target.id}`;
    setFilters({ ...filters, sort: sortField });
    filters.sort = sortField;

    await fetchPromotions(); // Refresh data with new sort order
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();

    const searchValue = filterData.search.trim().toLowerCase();
    const filteredPromotion = promotions.filter((promotion) =>
      promotion.name.toLowerCase().includes(searchValue)
    );

    setPromotions(filteredPromotion);
  };

  const handleFilterChange = async (e) => {
    const { name, value } = e.target;
    setFilterData({
      ...filterData,
      [name]: value,
    });

    if (name === "search" && value === "") {
      await fetchPromotions();
    }

    if (name === "type") {
      const updatedFilters = { ...filters, type: value };
      await fetchPromotions(updatedFilters);
    }
  };

  const handleEditMouseEnter = (index) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredRow(index);
  };

  const handleEditMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredRow(null);
    }, 250);
  };

  const handleTypeChange = (e) => {
    const { value } = e.target;
    setPromotionFormData((prev) => ({
      ...prev,
      type: value,
      amount: value === "FREE" ? null : "", // Reset amount if FREE is selected
    }));
  };

  const productOptions = productList?.length > 0 ? productList.map(row => ({ value: row.id, label: row.name, price: row.price })) : [];
  const selectedProductOption = productOptions.find(o => o.value === promotionFormData.product_id) || null;

  return (
    <>
      <MenuHeader />
      <div className="bloc-search">
        <div className="container">
          <div className="search">
            <div className="select">
              <select name="type" value={filters.type} onChange={handleFilterChange}>
                <option value="">All Types</option>
                <option value="PERCENT_OFF">Percent Off</option>
                <option value="FLATT_OFF">Flat Off</option>
                <option value="FREE_CREDITS">Free Credits</option>
                <option value="FREE">Free</option>
              </select>
            </div>
          </div>
          <button onClick={() => openModal()} className="btn-add-client">
            Add
          </button>
        </div>
      </div>

      <div className="container">
        <div className="search margeTop20">
          <form onSubmit={handleFilterSubmit} className="text-search">
            <input
              type="text"
              name="search"
              value={filterData.search}
              onChange={handleFilterChange}
              placeholder="Search Promotion Name"
              className="form-text"
            />
            <input type="submit" className="form-submit" value="Search" />
          </form>
        </div>
      </div>

      <div className="tableau-list">
        <div className="container">
          <table>
            <thead>
              <tr>
                <th className="sort-header active" id="name" onClick={handleSelectedSort}>Name</th>
                <th className="sort-header active" id="date_creation" onClick={handleSelectedSort}>Date of creation</th>
                <th className="sort-header active" id="date_validity" onClick={handleSelectedSort}>Date of validity</th>
                <th className="sort-header active" id="type" onClick={handleSelectedSort}>Type</th>
                <th className="sort-header active" id="promo.clients" onClick={handleSelectedSort}>Assigned to</th>
                <th className="sort-header active" id="total_usage" onClick={handleSelectedSort}>Usage</th>
                <th className="sort-header active" id="is_active" onClick={handleSelectedSort}>Status</th>
                <th >Actions</th>
              </tr>
            </thead>
            <tbody>
              {promotions?.length > 0 ? (
                promotions.map((promo, index) => (
                  <tr key={promo.id}>
                    <td>{promo.name}</td>
                    <td>{castDate(promo.date_creation)}</td>
                    <td>{castDate(promo.date_validity)}</td>
                    <td>{asTitle(promo.type)}</td>
                    <td>
                      {promo.clients?.map((client) => client.extra?.full_name).join(", ")}
                    </td>
                    <td>{promo.total_usage}</td>
                    <td className={promo.is_active ? "active" : "disabled"}>
                      {promo.is_active ? "Active" : "Disabled"}
                    </td>
                    <td>
                      <div
                        className="edit"
                        onClick={() => setHoveredRow((prev) => (prev === index ? null : index))}
                        style={{ position: "relative" }}
                        onMouseLeave={handleEditMouseLeave}
                        onMouseEnter={handleEditMouseEnter}
                      >
                        &#8942;
                        {hoveredRow === index && (
                          <div className="dropdown-menu">
                            <ul>
                              <li onClick={() => openModal(promo)}>Edit Promotion</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No hotels found.</td>
                </tr>
              )}
            </tbody>
            <tfoot >
              <tr>
                <td colSpan={8}>
                  <div className="paginator">
                    <select value={perPage} onChange={handlePerPageChange} className="per-page">
                      <option value={1}>1</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={250}>250</option>
                      <option value={500}>500</option>
                      <option value={1000}>1000</option>
                    </select>
                    <div className="total">Total Records: {total}</div>
                    <div className="pagination">
                      <button type="button" disabled={currentPage === 1} onClick={handleFirstPage}>{'<<'}</button>
                      <button type="button" disabled={currentPage === 1} onClick={handlePrevPage}>{'<'}</button>
                      <span>{currentPage} / {lastPage}</span>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleNextPage}>{'>'}</button>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleLastPage}>{'>>'}</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel={selectedPromotion ? "Edit Promotion" : "Create Promotion"}
      >
        <button className="close-button" onClick={closeModal}>
          <span className="close-icon">&times;</span>
        </button>

        <h2>{selectedPromotion ? "Edit Promotion" : "Add Promotion Code"}</h2>
        <div className="form-inscription">
          <form onSubmit={handleFormSubmit}>
            <div className="form-text">
              <label>Code string (auto-generated but editable)</label>
              <input type="text" name="code" value={promotionFormData.code} onChange={handleInputChange} />
            </div>

            <div className="form-text">
              <label>Name</label>
              <input type="text" name="name" value={promotionFormData.name} onChange={handleInputChange} />
            </div>

            <div className="form-text">
              <label>Date of creation*</label>
              <input type="date" name="date_creation" value={promotionFormData.date_creation} onChange={handleInputChange} />
            </div>

            <div className="form-text">
              <label>Date of validity*</label>
              <input type="date" name="date_validity" value={promotionFormData.date_validity} onChange={handleInputChange} />
            </div>

            <div className="form-select">
              <label>Type</label>
              <select name="type" value={promotionFormData.type} onChange={handleTypeChange} required>
                <option value="PERCENT_OFF">Percent Off</option>
                <option value="FLATT_OFF">Flat Off</option>
                <option value="FREE_CREDITS">Free Credits</option>
                <option value="FREE">Free</option>
              </select>
            </div>

            <div className="form-text">
              {promotionFormData.type === "PERCENT_OFF" && (
                <>
                  <label>Percentage</label>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="number"
                      name="amount"
                      value={promotionFormData.amount}
                      onChange={handleInputChange}
                      required
                      min="1"
                      max="100"
                      style={{ flex: 1 }}
                    />
                    <span style={{ marginLeft: "10px" }}>%</span>
                  </div>
                </>
              )}

              {(promotionFormData.type === "FLATT_OFF" || promotionFormData.type === "FREE_CREDITS") && (
                <>
                  <label>Amount</label>
                  <input
                    type="number"
                    name="amount"
                    value={promotionFormData.amount}
                    onChange={handleInputChange}
                    required
                  />
                </>
              )}

              {promotionFormData.type === "FREE" && (
                <>
                  <label>Select Product</label>
                  <Select
                    options={productOptions}
                    value={selectedProductOption}
                    onChange={(selectedOption) =>
                      setPromotionFormData((prev) => ({
                        ...prev,
                        product_id: selectedOption ? selectedOption.value : "",
                        amount: selectedOption.price,
                      }))
                    }
                    styles={customSelectStyles}
                    placeholder="Select a product"
                  />
                </>
              )}
            </div>

            <div className="form-select">
              <label>Assigned to</label>
              <select name="assigned_to" value={promotionFormData.assigned_to} onChange={handleInputChange} required>
                <option value="One client">One client</option>
                <option value="Group of clients">Group of clients</option>
                <option value="All">All clients</option>
              </select>
            </div>

            {(promotionFormData.assigned_to === "One client" || promotionFormData.assigned_to === "Group of clients") && (
              <div className="form-text">
                <label>Clients</label>
                <Select
                  isMulti={promotionFormData.assigned_to === "Group of clients"}
                  options={clientOptions}
                  value={selectedClients}
                  onChange={handleClientsChange}
                  styles={customSelectStyles}
                  placeholder="Select clients"
                />
              </div>
            )}

            <div className="form-text">
              <label>Total Usage</label>
              <input type="number" name="total_usage" value={promotionFormData.total_usage} onChange={handleInputChange} required />
            </div>

            <div className="form-text">
              <label>Usage per Customer</label>
              <input type="number" name="usage_per_customer" value={promotionFormData.usage_per_customer} onChange={handleInputChange} required />
            </div>

            <div className="champs-notif">
              <input type="checkbox" name="notifyClients" checked={promotionFormData.notifyClients} onChange={handleInputChange} />
              <label>Send notification to target clients</label>
            </div>

            <button type="submit" className="btn-submit">{selectedPromotion ? "Update Code" : "Create Code"}</button>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default Promotion;
